"use client";

import { Box, Typography } from "@mui/material";
import { generateMediaSrc } from "@v2/components/MediaV2";
import { memo, useCallback, useId, useMemo, useState } from "react";

type ButtonsLayout = "grid" | "row";

type ViewportSettings = {
    ratio: string | null;
    buttons: ButtonsLayout;
}

type ImageTabsLCItemType = {
    id: number;
    title: string;
    description: string;
    desktop_media_id: string | null;
    mobile_media_id: string | null;
    alt: string | null;
    show_in_site: boolean;
}

type ImageTabsLCSettingsType = {
    label: string | null;
    title: string | null;
    subtitle: string | null;
    note: string | null;
    default_stage: number;
    max_width: string | null;
    background_color: string | null;
    active_bg: string | null;
    active_fg: string | null;
    mobile?: ViewportSettings;
    tablet?: ViewportSettings;
    desktop?: ViewportSettings;
}

const DEFAULT_VIEWPORT: ViewportSettings = { ratio: "16 / 9", buttons: "row" };

const COLORS = {
    surface: '#FAF8F6',
    card: '#fff',
    cardBorder: '#E6DFD9',
    title: '#191513',
    subtitle: '#6E655E',
    description: '#5C534D',
    note: '#8B7F76',
    accent: '#BB202E',
    idleBg: '#FBF9F7',
    idleFg: '#5C534D',
    idleBorder: '#E0D7D0',
    activeBg: '#221C1A',
    activeFg: '#ffffff',
};

// Image width to request per viewport. Kept generous so the swap between tabs
// never shows a lower-quality frame than the initial one.
const REQUESTED_WIDTH = { mobile: 800, tablet: 1000, desktop: 1400 };

// theme.breakpoints.up('lg') and .between('sm2', 'lg'), written as the plain media
// queries a <source> understands.
const DESKTOP_MEDIA = '(min-width: 1200px)';
const TABLET_MEDIA = '(min-width: 768px)';

const ImageTabsLC = memo(function ImageTabsLC({ content }: {
    content: {
        settings: ImageTabsLCSettingsType;
        data: ImageTabsLCItemType[];
    };
}) {
    const { settings } = content;
    const baseId = useId();

    const items = useMemo(
        () => (content.data ?? []).filter(item => item?.show_in_site !== false),
        [content.data],
    );

    const defaultStage = useMemo(() => {
        const requested = Number(settings.default_stage);
        if (!Number.isFinite(requested)) return 0;
        return Math.min(Math.max(Math.trunc(requested), 0), Math.max(items.length - 1, 0));
    }, [settings.default_stage, items.length]);

    const [active, setActive] = useState(defaultStage);
    // An admin can delete the active item while the page is open; clamp instead
    // of rendering an empty panel.
    const activeIndex = Math.min(active, Math.max(items.length - 1, 0));

    // Resolved for every viewport at once, so the stage ratio and the button layout are
    // plain breakpoints. Reading them from a media query hook made the server render the
    // mobile shape and the browser correct it after hydration.
    const viewports = useMemo(() => ({
        mobile: { ...DEFAULT_VIEWPORT, ...settings.mobile },
        tablet: { ...DEFAULT_VIEWPORT, ...settings.tablet },
        desktop: { ...DEFAULT_VIEWPORT, ...settings.desktop },
    }), [settings.desktop, settings.tablet, settings.mobile]);

    const by = useCallback(<T, >(pick: (settings: ViewportSettings) => T) => ({
        xs: pick(viewports.mobile),
        sm2: pick(viewports.tablet),
        lg: pick(viewports.desktop),
    }), [viewports]);

    // All three widths, resolved on the server, so the media queries below pick one
    // before the first paint. Choosing with useMediaQuery instead meant the server —
    // where it is always false — emitted the mobile file, and every wider screen
    // downloaded that one, painted it, then replaced it after hydration.
    const sources = useMemo(() => items.map(item => {
        const wideId = item.desktop_media_id || item.mobile_media_id;
        const narrowId = item.mobile_media_id || item.desktop_media_id;
        const build = (mediaId: string | null, width: number) => (
            mediaId ? generateMediaSrc(mediaId, { width, quality: 88 }) : ""
        );

        return {
            desktop: build(wideId, REQUESTED_WIDTH.desktop),
            tablet: build(wideId, REQUESTED_WIDTH.tablet),
            mobile: build(narrowId, REQUESTED_WIDTH.mobile),
        };
    }), [items]);

    if (!items.length) return null;

    const activeBg = settings.active_bg || COLORS.activeBg;
    const activeFg = settings.active_fg || COLORS.activeFg;

    // The background spans the full section while the content stays centered,
    // the same split the reference design uses.
    const maxWidth = settings.max_width ? `${settings.max_width}px` : undefined;

    return (
        <Box sx={{
            bgcolor: settings.background_color || COLORS.surface,
            borderRadius: { xs: 3, sm2: 4 },
            px: { xs: 2.25, sm2: 3, lg: 4 },
            py: { xs: 4, sm2: 5, lg: 6 },
        }}>
            <Box sx={{ maxWidth, mx: maxWidth ? 'auto' : undefined }}>
                {settings.title && (
                    <Typography component="h2" sx={{
                        fontSize: { xs: 25, sm2: 30, lg: 40 },
                        fontWeight: 700,
                        color: COLORS.title,
                        lineHeight: 1.5,
                        letterSpacing: '-.4px',
                        m: 0,
                    }}>
                        {settings.title}
                    </Typography>
                )}

                {settings.subtitle && (
                    <Typography sx={{
                        fontSize: { xs: 14.5, lg: 15.5 },
                        color: COLORS.subtitle,
                        lineHeight: 1.9,
                        maxWidth: '54ch',
                        mt: 1.25,
                    }}>
                        {settings.subtitle}
                    </Typography>
                )}

                <Box sx={{
                    mt: settings.title || settings.subtitle ? { xs: 2.75, lg: 4.5 } : 0,
                    bgcolor: COLORS.card,
                    border: `1px solid ${COLORS.cardBorder}`,
                    borderRadius: { xs: 2.5, lg: 3 },
                    p: { xs: 2.25, sm2: 3, lg: 5 },
                }}>
                    {/* Every frame is mounted at once and only its opacity changes, so
                        switching tabs never waits on a network request. */}
                    <Box sx={{
                        display: 'grid',
                        width: '100%',
                        aspectRatio: by(v => v.ratio || undefined),
                        overflow: 'hidden',
                        borderRadius: 2,
                        '& > *': { gridArea: '1 / 1' },
                    }}>
                        {items.map((item, index) => (
                            <Box
                                key={item.id}
                                role="tabpanel"
                                id={`${baseId}-panel-${item.id}`}
                                aria-labelledby={`${baseId}-tab-${item.id}`}
                                sx={{
                                    position: 'relative',
                                    opacity: index === activeIndex ? 1 : 0,
                                    transition: 'opacity .45s cubic-bezier(.4,0,.2,1)',
                                }}
                            >
                                {sources[index].mobile && (
                                    <Box component="picture" sx={{ position: 'absolute', inset: 0, display: 'block' }}>
                                        <source media={DESKTOP_MEDIA} srcSet={sources[index].desktop} />
                                        <source media={TABLET_MEDIA} srcSet={sources[index].tablet} />
                                        <Box
                                            component="img"
                                            src={sources[index].mobile}
                                            alt={item.alt || item.title || ""}
                                            loading="eager"
                                            fetchPriority={index === defaultStage ? "high" : undefined}
                                            sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>

                    {/* Same stacking trick: the box keeps the height of the longest
                        description, so switching tabs never shifts the layout. */}
                    <Box sx={{
                        display: 'grid',
                        mt: 2,
                        maxWidth: '46ch',
                        mx: 'auto',
                        '& > *': { gridArea: '1 / 1' },
                    }}>
                        {items.map((item, index) => (
                            <Typography
                                key={item.id}
                                aria-hidden={index !== activeIndex}
                                sx={{
                                    fontSize: { xs: 14, lg: 15 },
                                    color: COLORS.description,
                                    lineHeight: 1.9,
                                    textAlign: 'center',
                                    opacity: index === activeIndex ? 1 : 0,
                                    transition: 'opacity .3s',
                                }}
                            >
                                {item.description}
                            </Typography>
                        ))}
                    </Box>

                    <Box
                        role="tablist"
                        sx={{
                            display: by(v => v.buttons === 'grid' ? 'grid' : 'flex'),
                            flexWrap: 'wrap',
                            gap: 1,
                            mt: 3,
                        }}
                    >
                        {items.map((item, index) => {
                            const isActive = index === activeIndex;

                            return (
                                <Box
                                    key={item.id}
                                    component="button"
                                    type="button"
                                    role="tab"
                                    id={`${baseId}-tab-${item.id}`}
                                    aria-selected={isActive}
                                    aria-controls={`${baseId}-panel-${item.id}`}
                                    onClick={() => setActive(index)}
                                    sx={{
                                        flex: by(v => v.buttons === 'row' ? '1 1 150px' : undefined),
                                        minHeight: 52,
                                        p: 1.5,
                                        borderRadius: 1.5,
                                        border: '1px solid',
                                        borderColor: isActive ? activeBg : COLORS.idleBorder,
                                        bgcolor: isActive ? activeBg : COLORS.idleBg,
                                        color: isActive ? activeFg : COLORS.idleFg,
                                        fontFamily: 'inherit',
                                        fontSize: 14.5,
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        transition: 'all .25s',
                                    }}
                                >
                                    {item.title}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {settings.note && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        mt: 2,
                        fontSize: { xs: 12, lg: 12.5 },
                        color: COLORS.note,
                        lineHeight: 1.8,
                    }}>
                        <Box sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: COLORS.accent,
                            flex: 'none',
                        }} />
                        {settings.note}
                    </Box>
                )}
            </Box>
        </Box>
    );
});

export default ImageTabsLC;
